import { useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, PAYROLL_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Icon } from "@/components/Icon";
import { TimeApprovalQueue } from "@/components/TimeApprovalQueue";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { usePayrollRun } from "@/hooks/usePayrollRun";
import { formatMoney } from "@/lib/format";
import { initialsForVendor } from "@/lib/category";
import { PAY_PERIOD_LABEL, TAX_YEAR } from "@/pages/payroll-fixtures";
import "./Payroll.css";

const VISIBLE_COUNT = 5;

type ApprovalState = "idle" | "approved";

/**
 * The "Review run" destination from the Dashboard's PayrollCard and the
 * Payroll sidebar/bottom-nav entry.
 *
 * The employee census is no longer a fixture. It comes from GET /api/demo/employees
 * (usePayrollRoster) — real rows in the `employee` table, tenant-scoped by row-level
 * security — where it used to be `PAYROLL_EMPLOYEES`, a twelve-element TypeScript array.
 * Everything computed FROM each person's gross — FICA, federal withholding, net pay — was
 * already real and still is: one POST /api/payroll/run to the live, tested
 * EasyFarm.PayrollDeductions engine, rendering whatever it returns.
 *
 * What remains declared as a fixture is the RUN, not the people: biweekly periods, the
 * tax year, the pay-period label, and the single filing status applied to every line
 * (payroll-fixtures.ts). Those are stated on the screen rather than implied.
 */
export function Payroll() {
  const roster = usePayrollRoster();
  const { rows, status, totals, erroredCount } = usePayrollRun(roster.employees);
  const [expanded, setExpanded] = useState(false);
  const [approval, setApproval] = useState<ApprovalState>("idle");

  const visibleRows = expanded ? rows : rows.slice(0, VISIBLE_COUNT);
  const hiddenCount = rows.length - VISIBLE_COUNT;
  const employeeCount = roster.employees?.length ?? 0;

  /*
   * <main>, not a div. AppShell's own comment already promised this — "each page
   * (Dashboard, Payroll, Reports, Capture) owns exactly one <main> of its own" — and the
   * implementation had drifted from it: Payroll, Reports and Capture rendered no landmark
   * at all, so the skip link landed on a focus container and a screen-reader user had no
   * way to jump to the primary content of three of the five owner screens.
   *
   * The axe gate did not catch it because `landmark-one-main` is MODERATE impact and the
   * gate filters to serious/critical — a deliberate, defensible choice (a gate that cries
   * wolf gets switched off) with a blind spot this fell straight into. Found by walking
   * the DOM of every route instead of trusting the gate.
   */
  return (
    <main className="payroll-page">
      <PageHeader
        title="Payroll run"
        subtitle={
          roster.status === "ready"
            ? `Sample pay period ${PAY_PERIOD_LABEL} · ${employeeCount} ${
                employeeCount === 1 ? "employee" : "employees"
              }`
            : `Sample pay period ${PAY_PERIOD_LABEL}`
        }
        action={
          <button
            type="button"
            className={`btn ${approval === "approved" ? "btn--ghost" : "btn--primary"}`}
            disabled={approval === "approved" || status === "loading" || roster.status !== "ready"}
            onClick={() => setApproval("approved")}
          >
            {approval === "approved" ? (
              <>
                <Icon name="check" size={15} strokeWidth={2.4} />
                Approved (preview)
              </>
            ) : (
              "Approve run"
            )}
          </button>
        }
      />

      <AreaTabs area="/payroll" tabs={PAYROLL_TABS} label="Payroll" />

      {approval === "approved" && (
        <p className="payroll-page__approval-note" role="status">
          Marked approved in this preview only — there's no payroll ledger behind this demo to
          post the run to, so nothing outside this screen changed.
        </p>
      )}

      <div className="payroll-page__grid">
        {/* data-state is the tester's ground truth for this section (rules/ui-breadcrumbs
            §1): loading / error / empty / ready, read off the DOM rather than inferred
            from whether some text happens to be on screen. */}
        <section
          className="payroll-page__roster"
          aria-labelledby="roster-heading"
          data-state={roster.status === "ready" && employeeCount === 0 ? "empty" : roster.status}
        >
          <h2 id="roster-heading" className="payroll-page__card-title">
            Employees
          </h2>

          {roster.status === "loading" && (
            <StateMessage tone="loading" title="Loading the payroll roster…" />
          )}

          {roster.status === "error" && (
            <StateMessage
              tone="error"
              title="Could not load the payroll roster."
              detail={`${roster.error ?? ""} Nothing is computed below — there is no honest payroll run without knowing who is on it.`}
            />
          )}

          {roster.status === "ready" && employeeCount === 0 && (
            <StateMessage
              tone="empty"
              title="Nobody is on this entity's payroll yet."
              detail="An empty roster is a real answer, not a failure — there is simply nothing to run."
            />
          )}

          {/* Named so a tester can address THIS list specifically: the app shell renders
              its own nav <ul>, and an unqualified listitem query would sweep both
              (rules/ui-breadcrumbs §2 — role + accessible name over a test id). */}
          <ul className="payroll-page__list" aria-labelledby="roster-heading">
            {visibleRows.map(({ employee, status: rowStatus, paycheck, error }) => (
              <li key={employee.id} className="payroll-page__row">
                {/* initialsForVendor is a plain name->initials transform despite
                    its name (lib/category.ts) — reused here rather than
                    duplicating the same regex/slice logic for employees. */}
                <span className="payroll-page__avatar" aria-hidden="true">
                  {initialsForVendor(employee.name)}
                </span>
                <span className="payroll-page__meta">
                  <span className="payroll-page__name">{employee.name}</span>
                  <span className="payroll-page__classification">
                    {employee.classification}
                    {employee.ficaExemptByLaw && (
                      <span className="payroll-page__badge">H-2A</span>
                    )}
                  </span>
                </span>
                {rowStatus === "loading" && (
                  <span className="payroll-page__amount payroll-page__amount--pending">
                    Calculating…
                  </span>
                )}
                {rowStatus === "error" && (
                  <span className="payroll-page__amount payroll-page__amount--error" role="alert">
                    {error}
                  </span>
                )}
                {rowStatus === "ready" && paycheck && (
                  <span className="payroll-page__amount">
                    {/*
                      Says what the number is. A sighted reader takes it from the column; a
                      screen-reader user got "Miguel A., H-2A, one thousand three hundred and
                      ninety-nine dollars and thirty-five cents" with no way to tell earnings
                      from take-home — and the summary directly below leads on "Gross wages",
                      which is the other reading.
                  
                      The pattern is already the app's own: the run summary uses a real
                      <dt>Net pay</dt>, the MN wage detail is a real table with headers, and
                      KpiCard.css reinforces meaning the same way. This list of people was the
                      one place a figure was left to speak for itself.
                    */}
                    <span className="visually-hidden">Net pay </span>
                    {formatMoney(paycheck.netPay)}
                  </span>
                )}
              </li>
            ))}
          </ul>

          {hiddenCount > 0 && (
            <button
              type="button"
              className="payroll-page__more"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show fewer" : `+ ${hiddenCount} more employees`}
            </button>
          )}
        </section>

        <section className="payroll-page__summary" aria-labelledby="summary-heading">
          <h2 id="summary-heading" className="payroll-page__card-title">
            Run summary
          </h2>

          {status === "loading" && !totals && roster.status !== "error" && (
            <StateMessage tone="loading" title="Calculating this run…" />
          )}

          {totals && (
            <dl className="payroll-page__totals">
              <div className="payroll-page__total-line">
                <dt>Gross wages</dt>
                <dd>{formatMoney(totals.gross)}</dd>
              </div>
              <div className="payroll-page__total-line">
                <dt>FICA / Medicare</dt>
                <dd>{formatMoney(totals.ficaMedicare)}</dd>
              </div>
              <div className="payroll-page__total-line">
                {/*
                  "Fed / state w/h" until 2026-09-08, over a figure with NO state component.
                  Nothing computes state income tax: StateWithholding on PaycheckInput is an
                  INPUT, this screen never sends one, and the engine's only bracket table is
                  StateWithholding.NorthDakota2025 — which has no callers, and is the wrong
                  state anyway (COMPANY_STATE is "MN", and both seeded entities are MN).

                  MEASURED against the returns: this total is $1,680.00, and Form 943 line 8
                  "Federal income tax withheld" is $43,680.00 = 1,680 × 26. The two agree to
                  the cent, which they can only do if the state share is zero. Per employee
                  too — Sarah T. nets 1,968.00 − 150.56 − 192.01, and 192.01 is her W-2 box 2
                  divided by 26. There is no room in the arithmetic for state tax.

                  Minnesota withholds state income tax. A farmer reading his own payroll
                  against this one would find the deduction missing.
                */}
                <dt>Federal w/h</dt>
                <dd>{formatMoney(totals.withholding)}</dd>
              </div>
              <div className="payroll-page__total-line payroll-page__total-line--big">
                <dt>Net pay</dt>
                <dd>{formatMoney(totals.netPay)}</dd>
              </div>
            </dl>
          )}

          {erroredCount > 0 && (
            <StateMessage
              tone="error"
              title={`${erroredCount} of ${rows.length} paychecks could not be calculated.`}
              detail="Totals above reflect only the employees the engine could compute — they are a real subtotal, not the whole run."
            />
          )}

          {/*
            THE HUB IS GONE, NOT RESTYLED (ADR 0009 §2: "No hub links... deleted, not
            restyled"). This section used to end in a "More payroll screens" nav of six
            `new`-badged buttons to Payroll returns, H-2A, Approved hours, Saved places,
            Job codes and Pay runs — the area tab strip above (`AreaTabs`) is now how a
            reader reaches Returns and People; Approved hours moved to Reports' own Hours
            tab; Saved places and Job codes moved under the Setup gear (ADR 0009 §2:
            "Saved places and job codes are setup, not payroll"); Pay runs is the area's
            own Runs tab.
          */}
        </section>

        {/* Between the run and its disclosure on purpose: approving hours is the step
            that turns "somebody says they worked" into a figure this run and Minnesota's
            quarterly wage detail both depend on, so it belongs beside the run rather than
            behind a nav item somebody has to remember to visit. */}
        <TimeApprovalQueue />
      </div>

      <p className="payroll-page__disclosure">
        Every figure above except gross wages is computed live by the same tested payroll engine
        that powers the rest of the product (biweekly, {TAX_YEAR}, filed Single for every
        employee — a real run would read each person's actual W-4 and schedule). The employees
        badged <strong>H-2A</strong> are correctly exempt from Social Security and Medicare (FICA)
        here — both the employee and employer share — per the federal rule that H-2A agricultural
        wages are not FICA wages; their federal income-tax withholding is unaffected, since that
        exemption doesn't extend to it. Two known gaps remain, both on the state side. The net
        pay above does <strong>not</strong> include the MN Paid Leave withholding, which a
        separate engine computes on its own screen; and it does <strong>not</strong> include
        Minnesota state income-tax withholding, which this demo does not compute at all — so
        these figures are a correct <strong>federal</strong> paycheck, not the final 2026 one.
      </p>
    </main>
  );
}
